import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Search, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { img } from "@/lib/media";
import PageHeader from "@/components/PageHeader";

const SUGGESTIONS = ["Find part number 21879834", "Which products are out of stock?", "How much did we sell today?", "How much is outstanding?", "Which customers have unpaid invoices?", "Which products are low on stock?"];

function inlineMd(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**")) ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
}
function renderRich(text) {
  const out = [];
  (text || "").split("\n").forEach((ln, i) => {
    const t = ln.trim();
    if (!t) { out.push(<div key={i} className="h-1.5" />); return; }
    if (/^---+$/.test(t)) { out.push(<hr key={i} className="border-zinc-200 my-1.5" />); return; }
    const h = t.match(/^(#{1,4})\s+(.*)$/);
    if (h) { out.push(<p key={i} className="font-bold text-zinc-900 mt-1">{inlineMd(h[2])}</p>); return; }
    const b = t.match(/^[-*]\s+(.*)$/);
    if (b) { out.push(<div key={i} className="flex gap-1.5"><span className="text-brand">•</span><span>{inlineMd(b[1])}</span></div>); return; }
    out.push(<p key={i}>{inlineMd(t)}</p>);
  });
  return out;
}

export default function AIAssistant() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([{ role: "ai", text: "Hi! I'm your Demodog World Inc. ERP Assistant. Ask me about products, UOMs, part numbers, stock, customers, invoices, payments, sales or outstanding amounts — I only use your live ERP data." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (q) => {
    const msg = (q ?? input).trim();
    if (!msg || loading) return;
    const history = messages.slice(-6).map((m) => ({ role: m.role === "user" ? "user" : "assistant", text: m.text }));
    setMessages((m) => [...m, { role: "user", text: msg }]); setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/query", { message: msg, history });
      setMessages((m) => [...m, { role: "ai", text: data.answer, results: data.results }]);
    } catch (err) { setMessages((m) => [...m, { role: "ai", text: formatApiError(err.response?.data?.detail) }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="h-full flex flex-col p-6">
      <PageHeader title="AI Assistant" subtitle="Grounded in your live truck-parts database" />
      <div className="flex-1 bg-white border border-zinc-200/70 rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] ${m.role === "user" ? "bg-brand text-white" : "bg-zinc-100 text-zinc-800"} rounded-2xl px-4 py-3`} data-testid={`ai-msg-${i}`}>
                {m.role === "ai" && <div className="flex items-center gap-1.5 mb-1 text-brand text-xs font-bold uppercase tracking-wider"><Sparkles size={13} />Demodog World Inc. AI</div>}
                <div className="text-[15px] leading-relaxed">{m.role === "ai" ? renderRich(m.text) : <p className="whitespace-pre-wrap">{m.text}</p>}</div>
                {m.results?.length > 0 && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`ai-results-${i}`}>
                    {m.results.map((r, j) => (
                      <div key={j} className="bg-white rounded-xl p-2.5 border border-zinc-200 flex gap-2.5" data-testid={`ai-product-${r.id || j}`}>
                        <div className="h-14 w-14 rounded-lg bg-zinc-100 overflow-hidden shrink-0">{r.image ? <img src={img(r.image)} alt={r.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-300 font-bold">{(r.name || "?").charAt(0)}</div>}</div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[13px] leading-tight text-zinc-800 truncate">{r.name}</p>
                          <p className="text-[11px] text-zinc-400 font-mono truncate">{r.brand} · {r.part_number || "—"}</p>
                          <p className="text-[12px] mt-0.5"><span className="font-bold">${Number(r.price || 0).toFixed(2)}</span> · <span className={r.stock > 0 ? "text-emerald-600" : "text-red-500"}>{r.stock > 0 ? `${r.stock} in stock` : "Out of stock"}</span></p>
                          <button onClick={() => navigate(`/products?q=${encodeURIComponent(r.part_number || r.name)}`)} data-testid={`ai-view-${r.id || j}`} className="mt-1 text-[11px] font-semibold text-brand flex items-center gap-1 hover:underline"><ExternalLink size={11} />View product</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="bg-zinc-100 rounded-2xl px-4 py-3 text-zinc-400 animate-pulse">Thinking…</div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-zinc-100 p-4">
          <div className="flex gap-2 mb-3 flex-wrap">
            {SUGGESTIONS.map((s) => (<button key={s} onClick={() => ask(s)} className="h-8 px-3 rounded-full bg-zinc-100 text-zinc-600 text-xs font-medium hover:bg-brand-soft hover:text-brand transition flex items-center gap-1"><Search size={12} />{s}</button>))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about parts, stock, invoices…" className="flex-1 h-12 px-4 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            <button data-testid="ai-send" disabled={loading} className="h-12 w-12 rounded-xl bg-brand text-white flex items-center justify-center active:scale-95 disabled:opacity-50"><Send size={18} /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
